import { CircularProgress } from '@mui/material';
import React, { useState } from 'react';
import { ProductVariantResponse } from '../../../types';
import { ImageZoomModal } from '../../embeddings/components/ImageZoomModal';

interface VariantSelectorProps {
  detectionKey: string;
  variants: ProductVariantResponse[];
  selectedVariant: ProductVariantResponse | undefined;
  isLoading: boolean;
  onSelectVariant: (detectionKey: string, variant: ProductVariantResponse) => void;
}

export const VariantSelector: React.FC<VariantSelectorProps> = ({
  detectionKey,
  variants,
  selectedVariant,
  isLoading,
  onSelectVariant,
}) => {
  const [zoomImageSrc, setZoomImageSrc] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-2">
        <CircularProgress size={16} />
        <span className="ml-2 text-xs text-gray-500">Loading variants...</span>
      </div>
    );
  }

  if (variants.length <= 1) {
    return null;
  }

  return (
    <div className="mt-2 border-t pt-2">
      <div className="text-xs font-medium text-gray-600 mb-1">
        Select variant ({variants.length} available)
      </div>
      <div className="space-y-1 max-h-48 overflow-y-auto">
        {variants.map((variant) => {
          const isSelected = selectedVariant?.upc === variant.upc;
          return (
            <label
              key={variant.upc}
              className={`flex items-center gap-2 p-1.5 rounded cursor-pointer transition-colors ${
                isSelected
                  ? 'bg-blue-50 border border-blue-200'
                  : 'hover:bg-gray-100 border border-transparent'
              }`}
            >
              <input
                type="radio"
                name={`variant-${detectionKey}`}
                checked={isSelected}
                onChange={() => onSelectVariant(detectionKey, variant)}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              {variant.frontImage && (
                <img
                  src={variant.frontImage}
                  alt={variant.name || variant.upc}
                  className="w-10 h-10 object-contain rounded border bg-white flex-shrink-0 cursor-zoom-in"
                  onClick={(e) => {
                    e.preventDefault();
                    setZoomImageSrc(variant.frontImage);
                  }}
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium truncate">{variant.name || 'Unknown'}</div>
                <div className="text-xs text-gray-500">{variant.upc}</div>
              </div>
            </label>
          );
        })}
      </div>
      {zoomImageSrc && (
        <ImageZoomModal
          isOpen={true}
          imageSrc={zoomImageSrc}
          onClose={() => setZoomImageSrc(null)}
        />
      )}
    </div>
  );
};
